import React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect, useSelector } from 'react-redux';

import { cn } from 'helpers/classnames';
import HtmlToReact from 'components/common/HtmlToReact';
import ProductUtils from 'helpers/ProductUtils';
import ProductSkuNumber from 'components/productdetail/ProductSkuNumber';
import useMartyContext from 'hooks/useMartyContext';
import type { AppState } from 'types/app';
import { selectIsFeaturePdpPaperCuts } from 'selectors/features';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';

import css from 'styles/components/productdetail/productDescription.scss';

export interface DescriptionItems {
  bulletPoints: string[];
  sizeCharts: string[];
}

interface OwnProps {
  allowCollapse: boolean;
  brandLogo: JSX.Element;
  descriptionItems?: DescriptionItems;
  defaultProductType: string;
  isExpanded: boolean;
  productId: string;
  isProductTypeShoesOrClothing?: boolean;
  productCardGenderDisplay?: string;
}
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = OwnProps & PropsFromRedux;

export const ProductDescription = ({
  allowCollapse,
  brandLogo,
  descriptionItems,
  defaultProductType,
  gcTerms,
  isExpanded,
  productId,
  isProductTypeShoesOrClothing,
  productCardGenderDisplay
}: Props) => {
  const { testId, marketplace: { domain } = {} } = useMartyContext();

  const { isInOneOfPdpPaperCutsTreatments } = useSelector(selectIsFeaturePdpPaperCuts);
  const listFontSizeClasses = isInOneOfPdpPaperCutsTreatments ? '[&_li]:text-base' : '[&_li]:text-sm sm:[&_li]:text-base';

  let featuredDescription;
  let remainingDescriptionItems: string[] = [];
  const isGiftCard = ProductUtils.isGiftCard(defaultProductType);
  if (descriptionItems && descriptionItems.bulletPoints.length > 0) {
    featuredDescription = descriptionItems.bulletPoints[0];
    remainingDescriptionItems = descriptionItems.bulletPoints.slice(1);
  }

  const is6pm = domain === '6pm';

  return (
    <div className={css.productDescription} itemProp="description" data-test-id={testId('productDescriptionSection')}>
      <div
        className={cn(
          css.description,
          {
            [css.more]: !isExpanded && allowCollapse && descriptionItems
          },
          listFontSizeClasses
        )}
      >
        {!isInOneOfPdpPaperCutsTreatments && (
          <div className="mb-6 flex items-center gap-2">
            {!isGiftCard && <div className={css.brandLogo}>{brandLogo}</div>}
            {isProductTypeShoesOrClothing && productCardGenderDisplay && !is6pm && <ProductSkuNumber productId={productId} />}
          </div>
        )}

        {descriptionItems ? (
          <ul>
            {featuredDescription && (
              <li className={css.featuredDescription} data-test-id={testId('featuredDescription')}>
                <HtmlToReact noContainer={true}>{featuredDescription}</HtmlToReact>
              </li>
            )}

            {is6pm && (
              <li className={css.descriptionSku} data-test-id={testId('descriptionSku')}>
                SKU: #<span itemProp="sku">{productId}</span>
              </li>
            )}

            {remainingDescriptionItems.map((item, index) => (
              <li key={`description_${index}`} data-test-id={testId('descriptionItem')}>
                <HtmlToReact noContainer={true}>{item}</HtmlToReact>
              </li>
            ))}
            {isGiftCard && gcTerms && (
              <li>
                <HtmlToReact noContainer={true}>{gcTerms}</HtmlToReact>
              </li>
            )}
            {isInOneOfPdpPaperCutsTreatments && !is6pm && (
              <li>
                <ProductSkuNumber productId={productId} />
              </li>
            )}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

export const mapStateToProps = (state: AppState) => ({
  gcTerms: state.headerFooter.content.Global.slotData.giftCardTerms?.gcTerms
});

const connector = connect(mapStateToProps);

const ProductDescriptionConnected = connector(ProductDescription);

export default withErrorBoundary('ProductDescription', ProductDescriptionConnected);
